import { useCallback, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { selectActiveMatter } from '@/matter/store/selectors'
import { MattersActions, MatterDialogType } from '@/matter/store/slice'
import { useCreateNewAgentChat } from '@/agent/conversations/hooks/useNewAgentChat'
import { isUnassignedMatter } from '@/matter/utils/is-unassigned-matter'
import { kMyDriveName, matterPaths } from '@/routing/paths'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { AuthContext } from '@/context/auth-context'
import { MatterJurisdictionEntrySource } from '@/analytics/enums/matter-jurisdiction.enums'

export function useMatterOverview() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const matter = useAppSelector(selectActiveMatter)
  const { createNewAgentChat } = useCreateNewAgentChat()
  const { userAccountData } = useContext(AuthContext)

  // source tags which surface opened the dialog, for attribution (defaults to unset, i.e. a direct edit).
  const handleEditMatter = useCallback(
    (source?: MatterJurisdictionEntrySource) => {
      if (!matter || isUnassignedMatter(matter)) return
      dispatch(MattersActions.openDialog({ dialogType: MatterDialogType.Edit, matterId: matter.id, source }))
    },
    [dispatch, matter]
  )

  const handleEditMatterJurisdiction = useCallback(() => handleEditMatter(MatterJurisdictionEntrySource.MatterDetailCta), [handleEditMatter])

  const handleUploadFiles = useCallback(() => {
    if (!matter || isUnassignedMatter(matter)) return
    navigate(matterPaths.files.folder(matter.id, kMyDriveName, matter.id))
  }, [navigate, matter])

  const handleUploadUserFiles = useCallback(() => {
    if (!matter || isUnassignedMatter(matter)) return
    navigate(matterPaths.userFiles.root(matter.id))
  }, [navigate, matter])

  const handleMatterSettings = useCallback(() => {
    if (!matter || isUnassignedMatter(matter)) return
    navigate(matterPaths.settings(matter.id))
  }, [navigate, matter])

  const handleDeleteMatter = useCallback(() => {
    if (!matter) return
    dispatch(MattersActions.openDialog({ dialogType: MatterDialogType.Delete, matterId: matter.id }))
  }, [dispatch, matter])

  const handleNewChat = useCallback(() => {
    if (!matter || isUnassignedMatter(matter)) return
    createNewAgentChat(true, matter.id)
  }, [createNewAgentChat, matter])

  const handleNavigateToAccount = useCallback(() => navigate('/dashboard/account'), [navigate])

  return {
    matter,
    handleEditMatter,
    handleEditMatterJurisdiction,
    handleUploadFiles,
    handleUploadUserFiles,
    handleMatterSettings,
    handleDeleteMatter,
    handleNewChat,
    handleNavigateToAccount,
    newFilesUxEnabled: kGetFeatureFlagStatus(kFeatureFlags.NEW_FILES_UX),
    showCollaborationUpsell: kGetFeatureFlagStatus(kFeatureFlags.COLLABORATION) && !userAccountData?.isOrgAccount,
  }
}
